import { useState } from 'react';
import { unifiedApiService } from '../../../services/unified';
import { useToast } from '../../../contexts/ToastContext';
import { useAuth } from '../../../hooks/useAuth';
import { env } from '../../../config/env';
import { useContentVersions, useActivateVersion } from '../../../hooks/unified/useContentVersionsQueries';
import type { ContentVersion } from '../../../hooks/unified/useContentVersionsQueries';

interface AIImageGeneratorProps {
  jobId: string;
  currentImageUrl?: string | null;
  onImageGenerated?: (imageUrl: string) => void;
  /** Use floating button style (30% opacity, matches other AI buttons) */
  floatingButton?: boolean;
}

const STYLE_OPTIONS = [
  { value: 'professional', label: 'Professional', description: 'Clean, corporate style' },
  { value: 'modern', label: 'Modern', description: 'Sleek, contemporary design' },
  { value: 'creative', label: 'Creative', description: 'Artistic, unique design' },
  { value: 'minimalist', label: 'Minimalist', description: 'Simple, elegant design' },
  { value: 'vibrant', label: 'Vibrant', description: 'Colorful, energetic design' },
] as const;

// Helper to get full image URL
const getImageUrl = (imageUrl: string | null | undefined): string | null => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('data:')) {
    return imageUrl;
  }
  return `${env.API_BASE_URL}${imageUrl}`;
};

export function AIImageGenerator({ 
  jobId, 
  currentImageUrl, 
  onImageGenerated,
  floatingButton = false 
}: AIImageGeneratorProps) {
  const { isAdmin } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<string>('professional');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'generate' | 'history'>('generate');

  // Fetch image version history
  const { data: versionsData, refetch: refetchVersions } = useContentVersions(jobId, 'image', {
    enabled: isOpen,
  });
  const activateMutation = useActivateVersion();

  if (!isAdmin) return null;

  const handleGenerate = async () => {
    // Prevent double-clicks
    if (isGenerating) return;
    
    setIsGenerating(true);
    setGeneratedImageUrl(null);

    try {
      const response = await unifiedApiService.jobs.generateJobImage(jobId, {
        style: selectedStyle as any,
        prompt: customPrompt.trim() || undefined,
      });

      if (response.success && response.image_url) {
        setGeneratedImageUrl(response.image_url);
        onImageGenerated?.(response.image_url);
        showSuccess('Image Generated', 'AI image has been generated and saved to the job.');
        // Refetch version history
        refetchVersions();
      } else {
        showError('Generation Failed', response.message || 'Failed to generate image');
      }
    } catch (error: any) {
      console.error('Image generation error:', error);
      showError('Generation Failed', error.message || 'Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleActivateVersion = async (version: ContentVersion) => {
    try {
      await activateMutation.mutateAsync({
        jobId,
        componentType: 'image',
        versionId: version.id,
      });
      showInfo('Version Activated', `Image version ${version.version_number} is now active.`);
      onImageGenerated?.(version.content);
      refetchVersions();
    } catch (error: any) {
      showError('Activation Failed', error.message || 'Failed to activate version');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Floating button style (matches AIEditableWrapper)
  const FloatingButton = () => (
    <button
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(true);
      }}
      className="opacity-30 hover:opacity-100 transition-all duration-200 
                 bg-purple-600 hover:bg-purple-700 rounded-full p-2 
                 shadow-lg z-20 hover:scale-110"
      title="Generate AI Image"
    >
      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    </button>
  );

  // Standard button style
  const StandardButton = () => (
    <button
      onClick={() => setIsOpen(true)}
      className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 
                 text-white rounded-lg transition-all duration-200 shadow-md hover:shadow-lg"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <span>Generate AI Image</span>
    </button>
  );

  return (
    <>
      {/* Trigger Button */}
      {floatingButton ? <FloatingButton /> : <StandardButton />}

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => !isGenerating && setIsOpen(false)}
            />

            {/* Modal Content */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white/20 rounded-lg">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">AI Image Generator</h2>
                      <p className="text-white/80 text-sm">Generate a professional image for this job</p>
                    </div>
                  </div>
                  <button
                    onClick={() => !isGenerating && setIsOpen(false)}
                    disabled={isGenerating}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                <button
                  onClick={() => !isGenerating && setActiveTab('generate')}
                  disabled={isGenerating}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors disabled:opacity-50 ${
                    activeTab === 'generate'
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Generate New
                </button>
                <button
                  onClick={() => !isGenerating && setActiveTab('history')}
                  disabled={isGenerating}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors disabled:opacity-50 ${
                    activeTab === 'history'
                      ? 'text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Version History ({versionsData?.total || 0})
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto relative">
                {/* Loading overlay during generation */}
                {isGenerating && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-lg">
                    <div className="text-center">
                      <svg className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <p className="text-purple-600 font-medium">Generating image...</p>
                      <p className="text-gray-500 text-sm mt-1">This may take 30-60 seconds</p>
                    </div>
                  </div>
                )}
                {activeTab === 'generate' ? (
                  <>
                    {/* Current/Generated Image Preview */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {generatedImageUrl ? 'Generated Image' : currentImageUrl ? 'Current Image' : 'Job Image'}
                      </label>
                      <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-100">
                        {(generatedImageUrl || currentImageUrl) ? (
                          <>
                            <img
                              src={getImageUrl(generatedImageUrl || currentImageUrl) || ''}
                              alt="Job image"
                              className="w-full h-48 object-cover"
                            />
                            {generatedImageUrl && (
                              <div className="absolute top-2 right-2 px-2 py-1 bg-green-500 text-white text-xs font-medium rounded-full">
                                New
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="w-full h-48 flex items-center justify-center text-gray-400">
                            <div className="text-center">
                              <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <p className="text-sm">No image yet</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Style Selection */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">Image Style</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {STYLE_OPTIONS.map((style) => (
                          <button
                            key={style.value}
                            onClick={() => setSelectedStyle(style.value)}
                            disabled={isGenerating}
                            className={`p-3 rounded-xl border-2 text-left transition-all ${
                              selectedStyle === style.value
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-gray-300'
                            } disabled:opacity-50`}
                          >
                            <div className="font-medium text-gray-900">{style.label}</div>
                            <div className="text-xs text-gray-500">{style.description}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Custom Prompt */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Custom Instructions (Optional)
                      </label>
                      <textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        disabled={isGenerating}
                        placeholder="Add specific details like 'Include a whiteboard with equations' or 'Show a team collaboration scene'..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none disabled:bg-gray-100"
                        rows={3}
                      />
                      <p className="text-xs text-gray-500">
                        The AI will automatically detect the job type (education, tech, healthcare, etc.) and create an appropriate scene.
                      </p>
                    </div>

                    {/* Info Box */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <div className="flex gap-3">
                        <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="text-sm text-blue-800">
                          <p className="font-medium">How it works</p>
                          <p className="mt-1">The AI analyzes the job title, description, and company to generate a relevant workplace image. For education jobs, it creates classroom scenes; for tech jobs, modern office environments, etc.</p>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  /* Version History Tab */
                  <div className="space-y-4">
                    {versionsData?.history && versionsData.history.length > 0 ? (
                      <div className="grid grid-cols-2 gap-4">
                        {versionsData.history.map((version) => {
                          const isActive = version.is_active === 1;
                          return (
                            <div
                              key={version.id}
                              className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                                isActive
                                  ? 'border-green-400 ring-2 ring-green-100'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <img
                                src={getImageUrl(version.content) || ''}
                                alt={`Version ${version.version_number}`}
                                className="w-full h-32 object-cover"
                              />
                              <div className="p-3 bg-white">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    v{version.version_number}
                                  </span>
                                  {isActive && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded">
                                      Active
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mb-2">
                                  {formatDate(version.created_at)}
                                </p>
                                {version.prompt_used && (
                                  <p className="text-xs text-gray-400 truncate mb-2" title={version.prompt_used}>
                                    {version.prompt_used}
                                  </p>
                                )}
                                {!isActive && (
                                  <button
                                    onClick={() => handleActivateVersion(version)}
                                    disabled={activateMutation.isPending}
                                    className="w-full px-3 py-1.5 text-xs font-medium text-purple-600 
                                               hover:bg-purple-50 rounded border border-purple-200 
                                               transition-colors disabled:opacity-50"
                                  >
                                    {activateMutation.isPending ? 'Activating...' : 'Use This Image'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <svg
                          className="w-16 h-16 mx-auto mb-4 text-gray-300"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-sm font-medium">No image versions yet</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Generate your first AI image to start building history
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setIsOpen(false)}
                  disabled={isGenerating}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  {activeTab === 'history' ? 'Close' : 'Cancel'}
                </button>
                {activeTab === 'generate' && (
                  <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span>Generate Image</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
